/**
 * Detach a panel into its own OS window.
 *
 * A commander on two monitors wants the map on one and a panel — the feed, the
 * roster, the briefing — on the other. This opens the panel's content in a real
 * separate window via window.open and portals React into it, so the same live
 * component renders there with no second data path to drift.
 *
 * The stylesheet is the catch: a new window starts blank, so the app's styles
 * are copied into it once at open. Closing the window (by the OS or the button)
 * brings the content home. If the browser blocks the pop-up, that is surfaced
 * rather than silently doing nothing.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

function copyStyles(source: Document, target: Document): void {
  for (const sheet of Array.from(source.styleSheets)) {
    try {
      const rules = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join('\n');
      const style = target.createElement('style');
      style.textContent = rules;
      target.head.appendChild(style);
    } catch {
      // Cross-origin sheet — link it instead of inlining.
      if (sheet.href) {
        const link = target.createElement('link');
        link.rel = 'stylesheet';
        link.href = sheet.href;
        target.head.appendChild(link);
      }
    }
  }
}

export interface PopoutState {
  open: boolean;
  blocked: boolean;
  toggle: () => void;
  render: (content: ReactNode) => ReactNode;
}

/**
 * The pop-out mechanics as a hook, so the display and the tests share one
 * source of truth for the open/close/blocked logic.
 */
export function usePopout(title: string): PopoutState {
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const windowRef = useRef<Window | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const popup = window.open('', title, 'width=520,height=680');
    if (!popup) {
      setBlocked(true);
      setOpen(false);
      return;
    }
    setBlocked(false);
    popup.document.title = title;
    const container = popup.document.createElement('div');
    popup.document.body.appendChild(container);
    copyStyles(document, popup.document);
    windowRef.current = popup;
    containerRef.current = container;

    // Closing the OS window returns the content to the main window.
    const onClose = () => setOpen(false);
    popup.addEventListener('beforeunload', onClose);

    return () => {
      popup.removeEventListener('beforeunload', onClose);
      popup.close();
      windowRef.current = null;
      containerRef.current = null;
    };
  }, [open, title]);

  const render = (content: ReactNode): ReactNode => {
    if (open && containerRef.current) return createPortal(content, containerRef.current);
    return content;
  };

  return { open, blocked, toggle: () => setOpen((v) => !v), render };
}

/** A panel with a pop-out control in its corner. */
export function PopoutPanel({ title, children }: { title: string; children: ReactNode }) {
  const popout = usePopout(title);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={popout.toggle}
        title={popout.open ? 'Bring back into the main window' : 'Pop out to a separate window'}
        aria-label={popout.open ? 'Dock panel' : 'Pop out panel'}
        className="absolute right-1 top-1 z-10 label-mono text-[8px] text-[color:var(--muted-foreground-2)] phone:hidden"
      >
        {popout.open ? '▤ DOCK' : '⤢ POP OUT'}
      </button>
      {popout.blocked ? (
        <p className="text-[10px] text-[color:var(--danger-foreground)]">
          The browser blocked the pop-out window. Allow pop-ups for this site.
        </p>
      ) : null}
      {popout.render(
        <div className={popout.open ? 'p-4' : undefined}>
          {popout.open ? <PoppedHeader title={title} onDock={popout.toggle} /> : null}
          {children}
        </div>,
      )}
    </div>
  );
}

function PoppedHeader({ title, onDock }: { title: string; onDock: () => void }) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <span className="label-mono text-[9px] text-[color:var(--muted-foreground-2)]">{title}</span>
      <button type="button" onClick={onDock} className="label-mono ml-auto text-[8px]">
        ▤ DOCK
      </button>
    </div>
  );
}
